import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { userAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { UsersRound, UserCog, CheckCircle2, XCircle, AlertTriangle, Clock, Calendar, Shield, ArrowUpRight, Pause, Mail, Phone } from 'lucide-react';

const TeamMembersPage = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [actionType, setActionType] = useState('');
  const [extensionDays, setExtensionDays] = useState(30);
  const [actionNotes, setActionNotes] = useState('');

  const { data: reporteesData, loading, refetch } = useApi(
    userAPI.getMyReportees,
    []
  );

  const { loading: actionLoading, execute: executeAction } = useAsyncAction();

  const employees = reporteesData?.reportees || reporteesData?.employees || [];

  const getEmployeeTypeBadge = (emp) => {
    const type = emp.employeeType || 'permanent';
    const probation = emp.probation;
    
    if (type === 'permanent') {
      return <Badge variant="success">Permanent</Badge>;
    }
    if (type === 'probation') {
      if (probation?.status === 'active') return <Badge variant="warning">Probation</Badge>;
      if (probation?.status === 'extended') return <Badge variant="warning">Probation (Extended)</Badge>;
      if (probation?.status === 'confirmed') return <Badge variant="success">Confirmed</Badge>;
      if (probation?.status === 'terminated') return <Badge variant="danger">Terminated</Badge>;
      return <Badge variant="warning">Probation</Badge>;
    }
    if (type === 'intern') {
      if (probation?.status === 'active') return <Badge variant="info">Intern</Badge>;
      if (probation?.status === 'extended') return <Badge variant="info">Intern (Extended)</Badge>;
      if (probation?.status === 'confirmed') return <Badge variant="success">Confirmed</Badge>;
      if (probation?.status === 'terminated') return <Badge variant="danger">Terminated</Badge>;
      return <Badge variant="info">Intern</Badge>;
    }
    if (type === 'trainee') {
      return <Badge variant="secondary">Trainee</Badge>;
    }
    return <Badge variant="secondary">{type}</Badge>;
  };

  const getProbationProgress = (emp) => {
    if (!emp.probation?.startDate || !emp.probation?.endDate) return null;
    const start = new Date(emp.probation.startDate);
    const end = new Date(emp.probation.endDate);
    const now = new Date();
    const total = end - start;
    const elapsed = now - start;
    const percentage = Math.min(100, Math.max(0, (elapsed / total) * 100));
    const daysRemaining = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    return { percentage, daysRemaining };
  };

  const canTakeAction = (emp) => {
    const type = emp.employeeType;
    const status = emp.probation?.status;
    return (type === 'probation' || type === 'intern') && 
           (status === 'active' || status === 'extended');
  };

  const handleAction = (emp, type) => {
    setSelectedEmployee(emp);
    setActionType(type);
    setExtensionDays(30);
    setActionNotes('');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      setError('');
      const data = { action: actionType, notes: actionNotes };
      if (actionType === 'extend') {
        data.extensionDays = extensionDays;
      }

      const result = await executeAction(
        userAPI.employeeTypeAction,
        selectedEmployee._id,
        data
      );

      if (result.success) {
        setSuccess(`Employee ${actionType === 'extend' ? 'probation extended' : actionType === 'confirm' ? 'confirmed as permanent' : 'terminated'} successfully!`);
        setShowActionModal(false);
        refetch();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || `Failed to ${actionType} employee`);
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UsersRound className="w-7 h-7 text-emerald-600" />
            My Team
          </h2>
          <p className="text-gray-500 mt-1">Manage your team members, probation & internship status</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{employees.length} team member{employees.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} />}

      {employees.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <UsersRound className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No team members assigned to you</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {employees.map((emp) => {
            const progress = getProbationProgress(emp);
            const showActions = canTakeAction(emp);

            return (
              <div
                key={emp._id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                        {emp.name?.charAt(0)?.toUpperCase() || 'E'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{emp.name}</h3>
                        <p className="text-xs text-gray-500">{emp.employeeId || '-'}</p>
                      </div>
                    </div>
                    {getEmployeeTypeBadge(emp)}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {/* Designation & Department */}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {emp.designation && (
                      <span className="bg-gray-100 px-2 py-1 rounded">{emp.designation}</span>
                    )}
                    {emp.department && (
                      <span className="bg-gray-100 px-2 py-1 rounded">{emp.department}</span>
                    )}
                  </div>

                  {/* Contact */}
                  <div className="space-y-1 text-sm text-gray-600">
                    {emp.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        <span className="truncate">{emp.email}</span>
                      </div>
                    )}
                    {emp.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <span>{emp.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Probation Progress */}
                  {progress && (emp.employeeType === 'probation' || emp.employeeType === 'intern') && 
                   (emp.probation?.status === 'active' || emp.probation?.status === 'extended') && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                          {formatDate(emp.probation?.startDate)} - {formatDate(emp.probation?.endDate)}
                        </span>
                        <span className={`font-medium ${progress.daysRemaining <= 7 ? 'text-red-600' : progress.daysRemaining <= 30 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {progress.daysRemaining} days left
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            progress.percentage >= 90 ? 'bg-red-500' : progress.percentage >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                      {emp.probation?.extendedCount > 0 && (
                        <p className="text-xs text-amber-600">
                          Extended {emp.probation.extendedCount} time(s) | +{emp.probation.totalExtendedDays} days
                        </p>
                      )}
                    </div>
                  )}

                  {/* Confirmed/Terminated Info */}
                  {emp.probation?.status === 'confirmed' && (
                    <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-2 py-1.5 rounded-lg">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Confirmed on {formatDate(emp.probation.confirmedDate)}</span>
                    </div>
                  )}
                  {emp.probation?.status === 'terminated' && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded-lg">
                      <XCircle className="w-3.5 h-3.5" />
                      <span>Terminated on {formatDate(emp.probation.terminatedDate)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {showActions && (
                  <div className="px-4 pb-4 flex gap-2">
                    <button
                      onClick={() => handleAction(emp, 'extend')}
                      className="flex-1 text-xs py-1.5 px-2 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors"
                    >
                      Extend
                    </button>
                    <button
                      onClick={() => handleAction(emp, 'confirm')}
                      className="flex-1 text-xs py-1.5 px-2 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => handleAction(emp, 'terminate')}
                      className="flex-1 text-xs py-1.5 px-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                    >
                      Terminate
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedEmployee && (
        <Modal
          isOpen={showActionModal}
          onClose={() => setShowActionModal(false)}
          title={
            actionType === 'extend' ? 'Extend Probation / Internship' :
            actionType === 'confirm' ? 'Confirm as Permanent' :
            'Terminate Employee'
          }
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold">
                {selectedEmployee.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-gray-900">{selectedEmployee.name}</p>
                <p className="text-sm text-gray-500">{selectedEmployee.employeeId} | {selectedEmployee.designation}</p>
              </div>
            </div>

            {actionType === 'extend' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extension Duration (Days)</label>
                <input
                  type="number"
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value) || 0)}
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            )}

            {actionType === 'confirm' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Confirm as Permanent Employee</p>
                    <p className="text-xs text-green-600 mt-1">
                      This will change the employee type from {selectedEmployee.employeeType} to permanent.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {actionType === 'terminate' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Terminate Employee</p>
                    <p className="text-xs text-red-600 mt-1">
                      This will mark the employee's probation/internship as terminated.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Add any notes about this action..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowActionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'terminate' ? 'danger' : actionType === 'confirm' ? 'success' : 'primary'}
                onClick={confirmAction}
                loading={actionLoading}
                className="flex-1"
              >
                {actionType === 'extend' ? 'Extend' : actionType === 'confirm' ? 'Confirm' : 'Terminate'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeamMembersPage;
